import { Heart, Users } from "lucide-react";
import React from "react";
import { motion } from "framer-motion";
import Modal from "../../../components/common/Modal";
import VoterAvatars from "../VoterAvatars";
import { TRAVEL_CATEGORIES, useTravelCategories } from "../constants";
import { useTranslation } from "react-i18next";

const IdeaDetailModal = ({ selectedIdea, onClose, handleVote, open }) => {
  const { t } = useTranslation();
  const categories = useTravelCategories();

  // Find the category label from the categories array
  const getCategoryLabel = () => {
    if (!selectedIdea) return "";

    const category = categories.find((cat) => cat.value === selectedIdea.category);

    return category?.label || t("ideas.detail.uncategorized");
  };

  const headerContent = (
    <div>
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-2xl tw-font-bold tw-text-transparent"
      >
        {selectedIdea?.destination || t("ideas.detail.no_destination")}
      </motion.h2>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="tw-mt-2 tw-flex tw-items-center tw-space-x-2"
      >
        <span className="tw-text-sm tw-text-gray-500">
          {t("ideas.detail.posted_by")} {selectedIdea?.author || t("ideas.detail.anonymous")}
        </span>
        <span className="tw-text-gray-300">•</span>
        <span className="tw-rounded-full tw-bg-gray-100 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-600">
          {getCategoryLabel()}
        </span>
      </motion.div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} headerContent={headerContent}>
      {selectedIdea?.images && selectedIdea.images.length > 0 && (
        <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
          {selectedIdea.images.map((image, index) => (
            <div key={index} className="tw-h-64 tw-overflow-hidden tw-rounded-xl">
              <img
                src={image}
                alt={`${selectedIdea.destination} - Image ${index + 1}`}
                className="tw-h-full tw-w-full tw-object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="tw-mb-8 tw-pr-2"
      >
        {selectedIdea?.description ? (
          <p className="tw-whitespace-pre-line tw-text-gray-600">{selectedIdea.description}</p>
        ) : (
          <p className="tw-italic tw-text-gray-400">{t("ideas.detail.no_description")}</p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="tw-flex tw-flex-col tw-space-y-4"
      >
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-space-x-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (selectedIdea?.id) {
                  handleVote(selectedIdea?.id);
                }
              }}
              className="tw-flex tw-items-center tw-space-x-2 tw-rounded-full tw-bg-rose-50 tw-px-4 tw-py-2 tw-transition-colors hover:tw-bg-rose-100"
            >
              <Heart
                className={`tw-h-5 tw-w-5 ${
                  selectedIdea?.hasVoted ? "tw-fill-rose-500 tw-text-rose-500" : "tw-text-rose-500"
                }`}
              />
              <span className="tw-font-medium tw-text-rose-500">
                {selectedIdea?.votes} {t("ideas.detail.votes")}
              </span>
            </motion.button>
          </div>
        </div>

        {selectedIdea?.voters && selectedIdea.voters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="tw-rounded-xl tw-bg-gray-50 tw-p-4"
          >
            <div className="tw-mb-3 tw-flex tw-items-center tw-space-x-3">
              <Users className="tw-h-5 tw-w-5 tw-text-gray-500" />
              <span className="tw-font-medium tw-text-gray-700">
                {t("ideas.detail.supporters")}
              </span>
            </div>
            <VoterAvatars voters={selectedIdea.voters} maxDisplay={8} />
            {selectedIdea.voters.length > 8 && (
              <p className="tw-mt-2 tw-text-sm tw-text-gray-500">
                {t("ideas.detail.more_supporters", {
                  count: selectedIdea.voters.length - 8,
                })}
              </p>
            )}
          </motion.div>
        )}
      </motion.div>
    </Modal>
  );
};

export default IdeaDetailModal;
